import { useRef, useState } from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { OverlayPanel } from "primereact/overlaypanel";
import GOOGLE_GROUP_MAPPINGS from "../../mappings_dir/google_group_mappings.json";
import {
  useLocalUser,
  useUpdateReport,
} from "../../pages/specialty-assistant/data";
import { useClientId } from "../../ClientConfig";
import { featureEnabled } from "../../client-features";
import { GoogleGroupIcon } from "./GoogleGroupIcon";
import styles from "./GoogleGroupAdminEdit.module.css";

type TeamOption = {
  label: string;
  value: string;
};

const TEAM_OPTIONS: TeamOption[] = GOOGLE_GROUP_MAPPINGS.map((entry) => ({
  label: entry.display_name,
  value: entry.google_group_email,
})).sort((a, b) => a.label.localeCompare(b.label));

export const GoogleGroupAdminEdit = ({
  reportId,
  googleGroupEmail,
}: {
  reportId: string;
  googleGroupEmail: string | null;
}) => {
  const localUser = useLocalUser();
  const clientId = useClientId();
  const op = useRef<OverlayPanel | null>(null);
  const updateReport = useUpdateReport(reportId);
  const [pendingValue, setPendingValue] = useState<string | null>(
    googleGroupEmail
  );

  const canEdit = featureEnabled(clientId, "edit-team", { user: localUser });

  if (!canEdit) {
    return <GoogleGroupIcon groupName={googleGroupEmail} />;
  }

  const onSave = () => {
    updateReport.mutate({
      googleGroupEmail: { new_google_group_email: pendingValue },
    });
    op.current?.hide();
  };

  return (
    <div className={styles.container}>
      {googleGroupEmail ? (
        <GoogleGroupIcon groupName={googleGroupEmail} />
      ) : (
        <span className={styles.unsetLabel}>No team</span>
      )}
      <Button
        icon="pi pi-pencil"
        text
        rounded
        severity="secondary"
        size="small"
        aria-label="Edit team"
        onClick={(e) => {
          setPendingValue(googleGroupEmail);
          op.current?.toggle(e);
        }}
      />
      <OverlayPanel ref={op} dismissable>
        <div className={styles.editor}>
          <div className={styles.editorLabel}>Team</div>
          <Dropdown
            value={pendingValue}
            options={TEAM_OPTIONS}
            onChange={(e) => setPendingValue(e.value)}
            placeholder="Select team"
            filter
            showClear
            className={styles.dropdown}
          />
          <div className={styles.editorActions}>
            <Button
              label="Cancel"
              text
              size="small"
              onClick={() => op.current?.hide()}
            />
            <Button
              label="Save"
              size="small"
              onClick={onSave}
              loading={updateReport.isPending}
            />
          </div>
        </div>
      </OverlayPanel>
    </div>
  );
};
